import {
  ExperimentConfig,
  ExperimentRun,
  LoadExperimentResponse,
  TrainResponse,
} from 'features/timeSeries/experiment/types'

const isExperimentRun = (value: unknown): value is ExperimentRun => {
  if (!value || typeof value !== 'object') return false
  const run = value as Partial<ExperimentRun>
  return (
    typeof run.id === 'string' &&
    typeof run.createdAt === 'string' &&
    typeof run.label === 'string' &&
    Boolean(run.config && typeof run.config === 'object') &&
    Boolean(run.result && typeof run.result === 'object')
  )
}

const comparableResult = (result: TrainResponse): string => {
  const value = {
    ...(result as TrainResponse & Record<string, unknown>),
  } as Record<string, unknown>
  // Model transport metadata changes when the selected run's scratch model is
  // copied onto the saved entry. It does not make this a different lab run.
  delete value.models_entry_id
  delete value.models_export_error
  return JSON.stringify(value)
}

const sameResult = (a: TrainResponse, b: TrainResponse): boolean => {
  if (a === b) return true
  return comparableResult(a) === comparableResult(b)
}

interface CurrentRun {
  id: string
  createdAt: string
  label: string
  config: ExperimentConfig
  result: TrainResponse | null | undefined
}

/**
 * Keep the first occurrence of each id (and therefore the persisted ordering),
 * then prepend the selected result only when it is not already represented.
 */
export const mergeExperimentRunHistory = (
  runs: unknown,
  current?: CurrentRun
): ExperimentRun[] => {
  const unique: ExperimentRun[] = []
  const ids = new Set<string>()
  if (Array.isArray(runs)) {
    for (const run of runs) {
      if (!isExperimentRun(run) || ids.has(run.id)) continue
      ids.add(run.id)
      unique.push(run)
    }
  }

  if (
    !current?.result?.ok ||
    unique.some((run) => sameResult(run.result, current.result!))
  ) {
    return unique
  }

  let id = current.id
  let suffix = 2
  while (ids.has(id)) {
    id = `${current.id}-${suffix}`
    suffix += 1
  }
  return [
    {
      id,
      createdAt: current.createdAt,
      label: current.label,
      config: current.config,
      result: current.result,
    },
    ...unique,
  ]
}

export const prepareRunsForSave = (
  runs: ExperimentRun[],
  config: ExperimentConfig,
  result: TrainResponse | null,
  createdAt = new Date().toISOString()
): ExperimentRun[] =>
  mergeExperimentRunHistory(runs, {
    id: `current-${Date.parse(createdAt)}`,
    createdAt,
    label: `Run ${runs.length + 1}`,
    config,
    result,
  })

/**
 * New saves hydrate runs exactly as stored. Legacy saves with only result.json
 * synthesize one stable comparison run so their diagnostics remain available.
 */
export const restoreExperimentRuns = (
  loaded: LoadExperimentResponse,
  config: ExperimentConfig,
  createdAt = new Date().toISOString()
): ExperimentRun[] =>
  mergeExperimentRunHistory(loaded.runs, {
    id: `saved-${loaded.entry_id}`,
    createdAt,
    label: loaded.name || 'Saved run',
    config,
    result: loaded.result,
  })
